import React, { useEffect, useRef } from 'react';
import { GlobalProps } from 'types';
import { ShopifyNext } from '@dotdev/next-components';
import * as Snippets from 'snippets'
import { trackPromotionClick } from 'helpers'

export const CustomCode: CustomCodeComponent = ({ section }) => {
  const { code, script, show_text_section: text_section_order } = section.settings;
  const textBlocks = section.blocks.filter(block => block.type === 'text');
  const scriptRef = useRef<HTMLDivElement>(null);
  const codeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (code && codeRef.current) {
      codeRef.current.innerHTML = code;
      const scripts = codeRef.current.getElementsByTagName('script');
      Array.from(scripts).forEach(script => {
        const newScript = document.createElement('script');
        Array.from(script.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
        newScript.appendChild(document.createTextNode(script.innerHTML));
        script.parentNode.replaceChild(newScript, script);
      });
    }
  }, [code]);

  useEffect(() => {
    if (script && scriptRef.current) {
      const scriptElement = document.createElement('script');
      scriptElement.src = script;
      scriptElement.async = true;
      
      if (script.includes('foursixty')) {
        scriptElement.setAttribute('data-feed-id', 'helen-kaminski');
        scriptElement.setAttribute('data-theme', 'lookbook_v2_6');
        scriptElement.setAttribute('data-page-size', '25');
      }

      scriptRef.current.appendChild(scriptElement);

      return () => {
        if (scriptRef.current) {
          scriptRef.current.removeChild(scriptElement);
        }
      };
    }
  }, [script]);

  const getVerticalAlignment = (vertical: string) => {
    switch (vertical) {
    case 'start': return 'justify-start';
    case 'end': return 'justify-end';
    default: return 'justify-center';
    }
  };

  const getHorizontalAlignment = (horizontal: string) => {
    switch (horizontal) {
    case 'start': return 'items-start';
    case 'end': return 'items-end';
    case 'between': return 'items-stretch';
    default: return 'items-center';
    }
  };

  const getTextAlignment = (alignment: string) => {
    switch (alignment) {
    case 'left': return 'text-left';
    case 'right': return 'text-right';
    default: return 'text-center';
    }
  };

  const renderTextBlock = (block) => {
    if (!block || Object.keys(block).length === 0) {
      return null;
    }

    return (
      <div key={block?.settings?.title} className={`w-full md:w-1/2 p-4 flex flex-col ${getVerticalAlignment(block.settings.vertical)} ${getHorizontalAlignment(block.settings.horizontal)}`}>
        <div className={getTextAlignment(block.settings.alignment)}>
          {block.settings.title && <h2 className="text-2xl font-bold mb-1-6">{block.settings.title}</h2>}
          {block.settings.description && <p className="mb-1-6">{block.settings.description}</p>}
          {block.settings.cta_text && block.settings.cta_link && (
            <Snippets.Button
              onClick={() => trackPromotionClick(
                [{
                  id: section.id,
                  name: block.settings.title,
                  creative: 'custom-code',
                }],
                block.settings.cta_link
              )}
              colour={'secondary-transparent'}
              className={`mt-2 md:mt-3 pt-1-1 pb-1 block ${block.settings.cta_alignment}`}
              title={block.settings.cta_text}
            >
              <Snippets.Heading size={'h5'} tag={'p'}>{block.settings.cta_text}</Snippets.Heading>
            </Snippets.Button>
          )}
        </div>
      </div>
    )
  };

  return (
    <section className="border-0 flex flex-wrap">
      {!text_section_order && renderTextBlock(textBlocks[0])}

      <div className={`w-full ${textBlocks.length ? 'md:w-1/2' : ''} p-4`}>
        {script && (
          <div
            ref={scriptRef}
            id="code_snippet_script"
          />
        )}
        {code && (
          <div
            ref={codeRef}
            id="code_snippet_code"
          />
        )}
      </div>

      {text_section_order && renderTextBlock(textBlocks[0])}
    </section>
  );
};

CustomCode.sectionId = 'custom_code';

export default CustomCode;

interface CustomCodeSettings {
  code: string;
  script: string;
  show_text_section: boolean;
}

interface CustomCodeProps extends GlobalProps {
  section: ShopifyNext.Section<CustomCodeSettings> & {
    blocks: any;
  };
}

interface CustomCodeComponent extends React.FC<CustomCodeProps> {
  sectionId: string;
}